import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, Button,
    Alert, InputAdornment, CircularProgress,
} from '@mui/material';
import {
    Email as EmailIcon,
    ArrowBack as BackIcon,
    CalendarMonth as LogoIcon,
    MarkEmailRead as SentIcon,
} from '@mui/icons-material';
import { auth } from '../services/api';

const ForgotPassword = () => {
    const navigate   = useNavigate();
    const [email,     setEmail]     = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error,     setError]     = useState('');
    const [loading,   setLoading]   = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await auth.forgotPassword(email);
            setSubmitted(true);
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'center', px:2, py:6 }}>
            <Box sx={{ width:'100%', maxWidth:420 }}>

                <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, overflow:'hidden', borderColor:'rgba(0,0,0,0.1)' }}>

                    {/* Brand header */}
                    <Box sx={{ background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', px:4, pt:5, pb:4, position:'relative', overflow:'clip' }}>
                        <Box sx={{ position:'absolute', top:-30, right:-20, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.07)', pointerEvents:'none' }} />
                        <Box sx={{ position:'absolute', bottom:-40, left:-10, width:120, height:120, borderRadius:'50%', background:'rgba(255,255,255,0.05)', pointerEvents:'none' }} />
                        <Box sx={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', gap:2 }}>
                            <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                                <Box sx={{ width:40, height:40, borderRadius:2, bgcolor:'rgba(255,255,255,0.18)', border:'1.5px solid rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                    <LogoIcon sx={{ fontSize:22, color:'white' }} />
                                </Box>
                                <Typography variant="h6" fontWeight={800} sx={{ color:'white', letterSpacing:-0.3 }}>Quuip</Typography>
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={800} sx={{ color:'white', lineHeight:1.2 }}>Forgot password?</Typography>
                                <Typography variant="body2" sx={{ color:'rgba(255,255,255,0.75)', mt:0.5 }}>We'll send a reset link to your inbox</Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Body */}
                    <Box sx={{ px:4, py:4, bgcolor:'white', display:'flex', flexDirection:'column', gap:3 }}>
                        {submitted ? (
                            <Box sx={{ textAlign:'center', py:2 }}>
                                <Box sx={{ width:64, height:64, borderRadius:'50%', background:'linear-gradient(135deg,#DCFCE7,#BBF7D0)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2.5 }}>
                                    <SentIcon sx={{ fontSize:32, color:'#16A34A' }} />
                                </Box>
                                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color:'#0F172A' }}>Check your inbox</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight:1.7, mb:3 }}>
                                    If that email is registered, a reset link has been sent. It may take a minute to arrive.
                                </Typography>
                                <Button startIcon={<BackIcon />} onClick={() => navigate('/login')}
                                    sx={{ fontWeight:700, color:'#7C3AED', borderRadius:2 }}>
                                    Back to sign in
                                </Button>
                            </Box>
                        ) : (
                            <>
                                {error && <Alert severity="error" sx={{ borderRadius:2 }}>{error}</Alert>}
                                <Box component="form" onSubmit={handleSubmit} sx={{ display:'flex', flexDirection:'column', gap:2.5 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Email address</Typography>
                                        <TextField fullWidth required type="email" size="small" autoFocus
                                            placeholder="The email on your account"
                                            value={email} onChange={e => setEmail(e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                                    </Box>
                                    <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}
                                        sx={{ fontWeight:700, borderRadius:2.5, py:1.5, background:'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)' }}>
                                        {loading ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Send Reset Link'}
                                    </Button>
                                </Box>
                                <Box sx={{ textAlign:'center', pt:1, borderTop:'1px solid', borderColor:'divider' }}>
                                    <Button startIcon={<BackIcon />} onClick={() => navigate('/login')}
                                        sx={{ fontWeight:600, color:'text.secondary', borderRadius:2, fontSize:'0.85rem' }}>
                                        Back to sign in
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default ForgotPassword;
